import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, approveVersion, commitVersion, rejectVersion } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import GlobalHeader from "../components/GlobalHeader";

interface PendingReviewItem {
  id: string;
  page_id: string;
  status: "pending_review" | "approved";
  author_type: string;
  author_id: string;
  author_name: string | null;
  content: string;
  commit_message: string | null;
  created_at: string;
  page_slug: string;
  page_title: string;
  space_slug: string;
  space_username: string;
  space_name: string;
}

export default function ReviewsPage() {
  const [items, setItems] = useState<PendingReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<PendingReviewItem[]>("/api/pending-reviews")
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <GlobalHeader activeTab="reviews" />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          Pending Reviews
        </h1>

        {loading && (
          <div className="space-y-3 animate-pulse">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No pending reviews. All caught up!
          </p>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="space-y-3">
            {items.map((item) => (
              <ReviewCard
                key={item.id}
                item={item}
                onUpdate={(id, newStatus) => {
                  if (newStatus === null) {
                    setItems((prev) => prev.filter((i) => i.id !== id));
                  } else {
                    setItems((prev) =>
                      prev.map((i) => i.id === id ? { ...i, status: newStatus } : i)
                    );
                  }
                }}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ReviewCard({
  item,
  onUpdate,
}: {
  item: PendingReviewItem;
  onUpdate: (id: string, newStatus: "pending_review" | "approved" | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [acting, setActing] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const renderedHtml = item.content ? renderMarkdown(item.content) : "";

  async function handleApprove() {
    setActing(true);
    setActionError(null);
    try {
      await approveVersion(item.id);
      onUpdate(item.id, "approved");
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(false);
    }
  }

  async function handleCommit() {
    setActing(true);
    setActionError(null);
    try {
      await commitVersion(item.id);
      onUpdate(item.id, null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(false);
    }
  }

  async function handleReject() {
    setActing(true);
    setActionError(null);
    try {
      await rejectVersion(item.id, rejectReason || undefined);
      onUpdate(item.id, null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(false);
    }
  }

  const statusBadge =
    item.status === "approved"
      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
      : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
      {/* Header row */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {item.page_title}
            </p>
            <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge}`}>
              {item.status === "approved" ? "approved" : "pending review"}
            </span>
            {item.author_name && (
              <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${
                item.author_type === "agent"
                  ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
              }`}>
                {item.author_type === "agent" ? "🤖 " : ""}{item.author_name}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {item.space_name} · {item.space_username}/{item.space_slug}/{item.page_slug}
            {item.commit_message && <> · <span className="italic">{item.commit_message}</span></>}
          </p>
        </div>
        <span className="text-gray-400 dark:text-gray-600 text-sm ml-4 shrink-0 select-none">
          {expanded ? "▾" : "▸"}
        </span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          {/* Content preview */}
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 max-h-96 overflow-y-auto">
            {renderedHtml ? (
              <div
                className="prose dark:prose-invert max-w-none text-sm"
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
              />
            ) : (
              <p className="text-gray-400 dark:text-gray-600 text-sm italic">No content</p>
            )}
          </div>

          {/* Action bar */}
          <div className="px-5 py-3 flex items-center gap-3 flex-wrap">
            {item.status === "pending_review" && (
              <button
                onClick={handleApprove}
                disabled={acting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {acting ? "..." : "Approve"}
              </button>
            )}

            {item.status === "approved" && (
              <button
                onClick={handleCommit}
                disabled={acting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {acting ? "..." : "Publish"}
              </button>
            )}

            {!showReject ? (
              <button
                onClick={() => setShowReject(true)}
                disabled={acting}
                className="px-4 py-2 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors"
              >
                Reject
              </button>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="flex-1 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400"
                />
                <button
                  onClick={handleReject}
                  disabled={acting}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {acting ? "..." : "Confirm"}
                </button>
                <button
                  onClick={() => { setShowReject(false); setRejectReason(""); }}
                  className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}

            <Link
              to={`/${item.space_username}/${item.space_slug}/${item.page_slug}/edit`}
              className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors ml-auto"
            >
              Open editor →
            </Link>

            {actionError && (
              <p className="w-full text-red-500 text-xs">{actionError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
